// Domain · EmployeeChip
//
// Accepts an `EmployeeJoin` (the FK-relation shape Supabase returns) and
// renders the canonical employee affordance — an Avatar + EntityLink in
// chip or inline variant. Owns the null + missing-label fallback so
// callers stop writing `r.employee?.id ?? r.employee_id ?? 0` ladders in
// their `data` mappers.
//
// Mirror of CompanyChip / ProductChip — the third entry in the
// well-known chip set powered by `@ds/data/entityRefRegistry`.

import React from "react";
import { withExportSpec } from "@ds/display/exportSpec";
import { absoluteAppUrl, getEntityRoute } from "@ds/navigation/navigation";
import { Space } from "antd";
import type { EmployeeJoin } from "@shared/types/employee";
import { useEntityLinkRenderer } from "@ds/display/entityLinkRendererContext";
import { Avatar } from "@ds/display/Avatar";
import { SPACE } from "@ds/tokens/palette";

// ─── Inputs ───────────────────────────────────────────────────────────────

export interface EmployeeChipInput {
  /**
   * Joined employee row from Supabase
   * (`r.logistics_employee`, `r.load_sales_employee`, …).
   * `null` / `undefined` / missing id renders an em-dash.
   */
  employee: EmployeeJoin | null | undefined;
  /** Rendering variant. Default `"inline"` for table density. */
  variant?: "chip" | "inline";
  /** Size scale. Default `sm`. */
  size?: "sm" | "md";
  /** `data-testid` on the root. Defaults to `"employee-chip"`. */
  testId?: string;
}

export type EmployeeChipProps = EmployeeChipInput;

// ─── Component ────────────────────────────────────────────────────────────

export function EmployeeChip({
  employee,
  variant = "inline",
  size = "sm",
  testId = "employee-chip",
}: EmployeeChipProps) {
  const renderLink = useEntityLinkRenderer();
  if (!employee?.id) {
    return <span data-testid={testId}>—</span>;
  }
  return (
    <Space size={SPACE.xs} align="center" data-testid={testId}>
      <Avatar
        name={employee.full_name ?? undefined}
        src={employee.thumbnail_photo_url ?? undefined}
        size={size}
      />
      {renderLink({
        type: "employee",
        id: employee.id,
        label: employee.full_name ?? `#${employee.id}`,
        variant: variant === "chip" ? "pill" : "inline",
        size,
      })}
    </Space>
  );
}

// ─── Excel export ─────────────────────────────────────────────────────────
//
// See CompanyChip — derivation reads the declared component, so the chip that
// renders the EntityLink has to carry the tag itself. The avatar is chrome and
// doesn't travel; the name is the cell's value. Like ProductChip there is no
// plain-text branch: without an id the chip paints an em-dash, so the export
// declines the cell rather than inventing a label.

withExportSpec<typeof EmployeeChip, EmployeeChipProps>(EmployeeChip, {
  type: "link",
  value: (p) => {
    const id = p.employee?.id;
    if (!id) return undefined;
    return p.employee?.full_name || `#${id}`;
  },
  hyperlink: (p) =>
    p.employee?.id ? absoluteAppUrl(getEntityRoute("employee", p.employee.id)) : undefined,
});
